import { useState } from 'react'

import {
  BenefitParticipationDecision,
  CompanyUserBenefitDetailsResponse,
  MakeOptInOptOutDecisionRequest,
  makeOptInOptOutDecision,
} from 'core'

import { Card, Col, OptInButton, OptOutButton, Row } from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'
import { useCommandRequestV2, useLoginUserV2 } from 'common/hooks'

import BenefitDetailsSummary from '../../../common/benefitSummary/BenefitSummary'
import ShopForPolicy from '../../../common/shopForPolicy/ShopForPolicy'
import OptOutConfirmModal from '../common/optOutConfirmModal'
import OptedOutReasonModal from '../common/optedOutReasonModal'

import './styles.less'

type OptInOptOutDecisionProps = {
  companyUserBenefitDetails: CompanyUserBenefitDetailsResponse
}

const OptInOptOutDecision = ({ companyUserBenefitDetails }: OptInOptOutDecisionProps) => {
  const { user } = useLoginUserV2()
  const { sendCommand: sendMakeDecision, isLoading: isMakeDecisionLoading } = useCommandRequestV2(
    (request: MakeOptInOptOutDecisionRequest) => makeOptInOptOutDecision(request),
  )

  const [showOptOutModal, setShowOptOutModal] = useState(false)

  const [showOptedOutReasonModal, setShowOptedOutReasonModal] = useState(false)

  const onOptIn = async () => {
    const result = await sendMakeDecision({
      planPeriodId: companyUserBenefitDetails.planPeriodId,
      decision: BenefitParticipationDecision.OptedIn,
    })
    if (result.completed) {
      window.location.reload()
    }
  }

  const onOptOut = async () => {
    const result = await sendMakeDecision({
      planPeriodId: companyUserBenefitDetails.planPeriodId,
      decision: BenefitParticipationDecision.OptedOut,
    })
    if (result.completed) {
      setShowOptedOutReasonModal(true)
    }
  }

  return (
    <>
      <Card className={'optIn-optOut-decision-wrap mb-16'}>
        <p>Welcome, {user.firstName}</p>
        <h2 className={'text-bold'}>Make your opt-in decision</h2>

        {companyUserBenefitDetails.maxDateToTakeBenefitParticipationDecision && (
          <p className={'mb-24'}>
            You need to decide whether to opt-in or opt-out of your ICHRA benefits. You have until{' '}
            <b>{parseZoneFormat(companyUserBenefitDetails.maxDateToTakeBenefitParticipationDecision)}</b> to make your
            decision or you will be automatically opted out.
          </p>
        )}

        <p className={'mb-24'}>
          If you have questions, please see{' '}
          <a
            target="_blank"
            href={
              'https://peoplekeephelpichra.zendesk.com/hc/en-us/articles/16670072300311-Getting-Started-With-Your-ICHRA-Benefit'
            }
          >
            How to get started with your ICHRA benefits
          </a>{' '}
          or contact us at <a href={'mailto:support@peoplekeep.com'}>support@peoplekeep.com</a>
        </p>

        <Row gutter={16} className={'mb-16'}>
          <Col span={12}>
            <div className={'decision-box'}>
              <h3>Why opt in?</h3>

              <ul>
                <li>Get reimbursed for your health insurance premiums and eligible out-of-pocket expenses.</li>
                <li>Get help with finding an insurance plan.</li>
                <li>
                  If you are eligible for a premium tax credit, you will be required to drop your premium tax credit.
                </li>
              </ul>

              <OptInButton onClick={onOptIn} loading={isMakeDecisionLoading} disabled={isMakeDecisionLoading} />
            </div>
          </Col>

          <Col span={12}>
            <div className={'decision-box'}>
              <h3>Why opt out?</h3>

              <ul>
                <li>lf you do not have and don’t intend to get an individual insurance policy, you should opt out.</li>
                <li>Keep your premium tax credit if this benefit is not considered "affordable"</li>
                {!companyUserBenefitDetails.hasBenefitStarted && (
                  <li>
                    You can change your mind and opt in any time before{' '}
                    {parseZoneFormat(companyUserBenefitDetails.maxDateToTakeBenefitParticipationDecision)}.
                  </li>
                )}
              </ul>

              <OptOutButton onClick={() => setShowOptOutModal(true)} />
            </div>
          </Col>
        </Row>

        <BenefitDetailsSummary
          planPeriodStartDate={companyUserBenefitDetails.planPeriodStartDate}
          planPeriodEndDate={companyUserBenefitDetails.planPeriodEndDate}
          primaryBenefitContactName={companyUserBenefitDetails.primaryBenefitContact.name}
          primaryBenefitContactEmail={companyUserBenefitDetails.primaryBenefitContact.workEmail}
          familyStatus={companyUserBenefitDetails.familyStatus.name}
          classDetails={companyUserBenefitDetails.classDetails.name}
          expenseType={companyUserBenefitDetails.classDetails.expenseType.name}
          monthlyAllowance={companyUserBenefitDetails.classDetails.monthlyAllowance}
        />
      </Card>

      <section className={'w-100 mb-16'}>
        <ShopForPolicy user={user} isIchra />
      </section>

      <OptOutConfirmModal
        visible={showOptOutModal}
        hasBenefitStarted={companyUserBenefitDetails.hasBenefitStarted}
        maxDateToTakeBenefitParticipationDecision={companyUserBenefitDetails.maxDateToTakeBenefitParticipationDecision}
        onOk={onOptOut}
        okButtonDisabled={isMakeDecisionLoading}
        okButtonLoading={isMakeDecisionLoading}
        onCancel={() => setShowOptOutModal(false)}
      />

      <OptedOutReasonModal
        visible={showOptedOutReasonModal}
        maxDateToTakeBenefitParticipationDecision={companyUserBenefitDetails.maxDateToTakeBenefitParticipationDecision}
        hasBenefitStarted={companyUserBenefitDetails.hasBenefitStarted}
        benefitEligibilityDate={companyUserBenefitDetails.benefitEligibilityDate}
        planPeriodEndDate={companyUserBenefitDetails.planPeriodEndDate}
        planPeriodId={companyUserBenefitDetails.planPeriodId}
        companyUserId={user.companyUserId}
        onCancel={() => {
          setShowOptedOutReasonModal(false)
          setShowOptOutModal(false)
          window.location.reload()
        }}
      />
    </>
  )
}
export default OptInOptOutDecision
